<template>
  <div class="measure-config-options">
    <b-field
      class="input-field"
      label="Measure time range (optional):">
      <TimeRangeEditor
        v-model="inputs.time_range"
        :default-option-label="context.timeRangeDefaultLabel || 'Use report default'"
        :default-time-range-name="context.timeRangeDefaultName || ''"
        :default-time-range="context.tableDefaultTimeRange || null"
        @example-date-change="timeRangeExampleDate = $event" />
    </b-field>

    <TimeTransformEditor
      :offset-days="inputs.offset_days || 0"
      :repeat-config="inputs.repeat_config"
      :effective-time-range="inputs.time_range || context.tableDefaultTimeRange || null"
      :example-report-date="timeRangeExampleDate"
      :max-duration-days="maxMeasureDurationDays"
      @update:offsetDays="$set(inputs, 'offset_days', $event)"
      @update:repeatConfig="$set(inputs, 'repeat_config', $event)" />

    <!-- <MeasureTimeRange v></MeasureTimeRange> -->

    <div class="more-options-section mt-4">
      <span
        class="more-options-toggle"
        @click="moreOptionsOpen = !moreOptionsOpen">
        {{ moreOptionsOpen ? '- less options' : '+ more options' }}
        <span
          v-if="!moreOptionsOpen && activeOptionCount > 0"
          class="active-count">
          ({{ activeOptionCount }})
        </span>
      </span>

      <div
        v-if="moreOptionsOpen"
        class="more-options-body mt-3">
        <b-switch
          v-if="showFutureMeasure"
          v-model="enableFutureMeasure"
          class="mb-4"
          type="is-info">
          Use a different measure for data after the report date
        </b-switch>
        <b-field
          v-if="showFutureMeasure && enableFutureMeasure"
          class="input-field">
          Future measure:
          <CompanyMeasureRefSelect v-model="inputs.future_measure" />
        </b-field>

        <hr v-if="showFutureMeasure && hasDimensions" />

        <template v-if="hasDimensions">
          <b-switch
            v-model="inputs.is_pct_total"
            class="mb-4"
            type="is-info"
            @input="changePctTotalToggle">
            Calculate this measure as a percentage of a total value
          </b-switch>
          <div
            v-if="inputs.is_pct_total"
            class="pct-total-dimensions">
            <p class="pct-total-label">
              Limit Total to:
            </p>
            <div class="select-actions">
              <b-button
                type="is-text"
                size="is-small"
                @click="selectAllPctDimensions">
                Select All
              </b-button>
              <b-button
                type="is-text"
                size="is-small"
                @click="clearAllPctDimensions">
                Clear All
              </b-button>
            </div>
            <div
              v-for="(option, i) in pctTotalDimensionOptions"
              :key="i"
              class="pct-total-item">
              <b-checkbox
                :value="pctTotalsDimensionsSelections[i]"
                @input="togglePctDimension(i, $event)">
                {{ option.name || option.key }}
              </b-checkbox>
            </div>
          </div>
        </template>

        <hr v-if="hasDimensions || showFutureMeasure" />

        <b-switch
          v-model="enableAverage"
          class="mb-4"
          type="is-info">
          Calculate this measure as an average
        </b-switch>
        <b-field
          v-if="enableAverage"
          class="input-field"
          label="Average by:">
          <CompanyDimensionRefSelect v-model="inputs.avg_by_dimension" />
        </b-field>

        <b-switch
          v-model="enableMax"
          class="mb-4"
          type="is-info">
          Use this measure's best single value, such as the record day
        </b-switch>
        <b-field
          v-if="enableMax"
          class="input-field"
          label="Max by:">
          <CompanyDimensionRefSelect v-model="inputs.max_by_dimension" />
        </b-field>

        <hr />

        <b-switch
          v-model="toggleCompFilterType"
          class="mb-4"
          type="is-info">
          Filter this measure for comparable stores based on their date of opening
        </b-switch>
        <b-field
          v-if="toggleCompFilterType"
          class="input-field"
          label="Filter this measure for:">
          <singleselect
            v-model="inputs.comp_filter_type"
            :append-to-body="true"
            :options="compFilterTypeOptions"
            :return-as-object="false"
            :enable-search="false"
            label="label"
            track-by="key"
            header-placeholder="Select filter type" />
        </b-field>
      </div>
    </div>

    <hr />
    <FiltersTab
      :value="inputs.filters"
      :errors="childErrors('filters', errors)"
      :mode="context.filterModes ? resolvedFilterMode : null"
      :row-filters="context.rowFilters || []"
      :default-time-range="inputs.time_range || context.tableDefaultTimeRange || null"
      @input="$set(inputs, 'filters', $event)"
      @update:mode="setFilterMode" />
  </div>
</template>

<script>
  import singleselect from '@/components/ui/singleselect.vue'
  import FiltersTab from '@/components/pdf_report/table_module/table_rows/FiltersTab'
  import { childErrors as _childErrors } from '@/components/reports/config/common/validation'
  import ModuleConfigMixin from '@/components/reports/config/modules/common/ModuleConfigMixin'
  import TimeRangeEditor from '@/components/reports/config/modules/common/TimeRangeEditor'
  import TimeTransformEditor from '@/components/reports/config/modules/common/TimeTransformEditor'
  import { computeMaxDurationDays } from './timeRangeUtils'
  import CompanyDimensionRefSelect from '@/components/reports/config/dimensions/CompanyDimensionRefSelect'
  import CompanyMeasureRefSelect from '@/components/reports/config/measures/CompanyMeasureRefSelect'

  export default {
    name: 'MeasureConfigOptions',
    components: {
      singleselect,
      FiltersTab,
      CompanyDimensionRefSelect,
      TimeRangeEditor,
      TimeTransformEditor,
      CompanyMeasureRefSelect,
    },
    mixins: [ModuleConfigMixin,],
    props: {
      errors: {
        type: Array,
        default: () => [],
      },
      showFutureMeasure: {
        type: Boolean,
        default: true,
      },
    },
    data () {
      return {
        timeRangeExampleDate: null,
        moreOptionsOpen: false,
        compFilterTypeOptions: [
          {
            key: 'comp',
            label: 'Comparable stores only',
          },
          {
            key: 'non_comp',
            label: 'Non-comparable store only',
          },
        ],
      }
    },
    computed: {
      // Matches the renderer's reading of a config written before the mode
      // existed: filters present meant replace, none meant the total was
      // summing its row.
      resolvedFilterMode () {
        if (this.inputs?.filter_mode) return this.inputs.filter_mode
        return this.inputs?.filters?.length ? 'replace' : 'inherit'
      },
      activeOptionCount () {
        let count = 0
        if (this.enableFutureMeasure && this.showFutureMeasure) count++
        if (this.inputs?.is_pct_total) count++
        if (this.enableAverage) count++
        if (this.toggleCompFilterType) count++
        return count
      },
      hasDimensions () {
        return (this.context?.reportDimensions || []).length > 0
      },
      maxMeasureDurationDays () {
        const ranges = this.context?.allMeasureTimeRanges
        if (!ranges || !ranges.length) return null
        const availableDates = this.$store?.getters['datePicker/availableDates'] || []
        const yesterday = this.timeRangeExampleDate || new Date()
        let maxDays = 0
        ranges.forEach(tr => {
          if (!tr) return
          let days = null
          if (tr.type === 'RelativeDateRange') {
            days = tr.duration_days || 0
          } else if (tr.key) {
            days = computeMaxDurationDays(tr.key, yesterday, availableDates)
          }
          if (days && days > maxDays) maxDays = days
        })
        return maxDays || null
      },
      enableFutureMeasure: {
        get () {
          return ![null, undefined,].includes(this.inputs.future_measure)
        },
        set (enable) {
          this.$set(this.inputs, 'future_measure', enable ? {} : undefined)
        },
      },
      enableAverage: {
        get () {
          return ![null, undefined,].includes(this.inputs.avg_by_dimension)
        },
        set (enable) {
          this.$set(this.inputs, 'avg_by_dimension', enable ? {} : undefined)
          // The API rejects a measure that is both an average and a max.
          if (enable) this.$set(this.inputs, 'max_by_dimension', undefined)
        },
      },
      enableMax: {
        get () {
          return ![null, undefined,].includes(this.inputs.max_by_dimension)
        },
        set (enable) {
          this.$set(this.inputs, 'max_by_dimension', enable ? {} : undefined)
          if (enable) this.$set(this.inputs, 'avg_by_dimension', undefined)
        },
      },
      companyDimensionOptions () {
        return this.$store.getters['app/reportsConfig/companyDimensionOptions']
      },
      pctTotalDimensionOptions () {
        return this.context.reportDimensions
      },
      pctTotalsDimensionsSelections () {
        const selections = {}
        const dims = this.inputs.pct_total_dimensions || []
        ;(this.pctTotalDimensionOptions || []).forEach((opt, i) => {
          selections[i] = dims.some(d => d.key === opt.key)
        })
        return selections
      },
      toggleCompFilterType: {
        get () {
          return ['comp', 'non_comp',].includes(this.inputs.comp_filter_type)
        },
        set (toggle) {
          if (toggle) {
            this.inputs.comp_filter_type = 'comp'
          } else {
            this.inputs.comp_filter_type = 'none'
          }
        },
      },
    },
    mounted () {
      if (this.activeOptionCount > 0) {
        this.moreOptionsOpen = true
      }
    },
    beforeDestroy () {},

    methods: {
      setFilterMode (mode) {
        this.$set(this.inputs, 'filter_mode', mode)
      },
      childErrors: _childErrors,
      togglePctDimension (i, selected) {
        const pctTotalDims = []
        ;(this.pctTotalDimensionOptions || []).forEach((opt, index) => {
          const isSelected = index === i ? selected : this.pctTotalsDimensionsSelections[index]
          if (isSelected) pctTotalDims.push(opt)
        })
        this.inputs.pct_total_dimensions = pctTotalDims
      },

      changePctTotalToggle () {
        this.inputs.pct_total_dimensions = []
      },
      selectAllPctDimensions () {
        this.inputs.pct_total_dimensions = [...(this.pctTotalDimensionOptions || []),]
      },
      clearAllPctDimensions () {
        this.inputs.pct_total_dimensions = []
      },
    },
  }
</script>

<style lang="scss" scoped>
.input-field {
  margin: 24px 0;
}

.more-options-section {
  .more-options-toggle {
    font-size: 13px;
    color: #888;
    cursor: pointer;

    &:hover {
      color: #555;
    }

    .active-count {
      color: #999;
      font-size: 12px;
    }
  }

  .more-options-body {
    hr {
      margin: 12px 0;
      border: none;
      border-top: 1px solid #eee;
    }
  }
}

.pct-total-dimensions {
  margin-left: 4px;

  .pct-total-label {
    font-size: 13px;
    color: #666;
    margin-bottom: 6px;
  }

  .select-actions {
    display: flex;
    gap: 4px;
    margin-bottom: 4px;
  }

  .pct-total-item {
    margin-bottom: 4px;
  }
}
</style>
